package org.vreo.devicehub.features.roi.presentation

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import androidx.compose.ui.geometry.Offset

import org.vreo.devicehub.features.core.presentation.components.byteArrayToBitmap.toImageBitmap
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.device.device.DeviceAuthentication
import org.vreo.devicehub.features.core.domain.device.imageRequest.StaticImageRequest
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.core.domain.device.roi.Roi
import org.vreo.devicehub.features.roi.domain.RoiRepository

/**
 * ROI view model
 *
 * @property authentication user and target device authentication
 * @property repository ROI repository for accessing remote data and control device
 * @property maxRoiCount maximum number of ROI regions allowed for the device
 * @property autoLoad Auto background image load on startup
 */
class RoiViewModel(
    private val authentication: DeviceAuthentication,
    private val repository: RoiRepository,
    private val maxRoiCount: Int = 3,
    private val autoLoad: Boolean = true
) : ViewModel() {
    private val _state = MutableStateFlow(RoiState(isDeviceActive = authentication.isDeviceActive))
    val state = _state
        .onStart {
            if (autoLoad) {
                updateImage()
                getRoiConfiguration()
            }
        }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5000L),
            initialValue = _state.value
        )

    /**
     * On action event handler
     *
     * @param action action to handle
     */
    fun onAction(action: RoiActions) {
        when (action) {
            is RoiActions.OnClearError -> {
                _state.update {
                    it.copy(error = null)
                }
            }

            is RoiActions.OnSaveRoi -> {
                saveRoiConfiguration()
            }

            is RoiActions.OnRefreshImage -> {
                updateImage()
            }

            is RoiActions.OnDismissRoiEditor -> {
                _state.update { it.copy(showRoiEditor = false) }
            }

            is RoiActions.OnAddRoi -> {
                addRoi()
            }

            is RoiActions.OnClickedRoi -> {
                _state.update { it.copy(selectedRoi = action.roi) }
            }

            is RoiActions.OnClickedEditRoi -> {
                _state.update {
                    it.copy(
                        selectedRoi = action.roi,
                        showRoiEditor = true
                    )
                }
            }

            is RoiActions.OnUpdateRoiDetails -> {
                updateRoiDetails(action.roi)
            }

            is RoiActions.OnUpdateRoiPoints -> {
                updateRoiPoints(
                    id = action.id,
                    points = action.points
                )
            }

            is RoiActions.OnDeleteRoi -> {
                deleteRoi(action.id)
            }
        }
    }

    /**
     * Update ROI background image used latest image/frame captured by the target device
     */
    private fun updateImage() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        val currentState = _state.value
        // Check if device is active, if not display error
        if (!currentState.isDeviceActive) {
            _state.update {
                it.copy(
                    isLoading = false,
                    image = null,
                    error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()
                )
            }
            return@launch
        }

        runCatching {
            val res = repository.getStaticImage(
                token = authentication.userToken,
                deviceId = authentication.deviceId,
                config = StaticImageRequest()
            )

            when (res) {
                is Result.Success -> {
                    val image = try {
                        res.data.toImageBitmap()
                    } catch (_: Exception) {
                        null
                    }

                    _state.update {
                        it.copy(
                            isLoading = false,
                            image = image
                        )
                    }
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = res.error.toUiText()
                        )
                    }
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Add new ROI to list. New ROI will be added only if max ROI count is not reached
     */
    private fun addRoi() {
        try {
            val currentState = _state.value
            val length = currentState.roi.size

            if (length >= maxRoiCount) {
                _state.update {
                    it.copy(error = DataError.Local.INVALID_STATE.toUiText())
                }
                return
            }

            // Find the smallest available ID in range of 1-10, if non is available return
            val existingIds = currentState.roi.map { it.id }.toSet()
            val nextId = (1..10).firstOrNull { it !in existingIds } ?: return

            // Add new ROI
            val rois = currentState.roi.toMutableList()
            rois.add(Roi(id = nextId))
            _state.update { it.copy(roi = rois) }

        } catch (_: Exception) {
            _state.update {
                it.copy(error = DataError.Local.UNKNOWN.toUiText())
            }
        }
    }

    /**
     * Update ROI details based on its ID
     *
     * @param roi ROI with updated data to be applied
     */
    private fun updateRoiDetails(roi: Roi) {
        try {
            _state.update { currentState ->
                val updatedList = currentState.roi.map {
                    if (it.id == roi.id) roi else it
                }
                currentState.copy(
                    roi = updatedList,
                    selectedRoi = if (currentState.selectedRoi?.id == roi.id) roi else currentState.selectedRoi
                )
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(error = DataError.Local.UNKNOWN.toUiText())
            }
        }
    }

    /**
     * Update roi points based on its ID
     *
     * @param id ID of the ROI to update
     * @param points coordinates to be applied to the ROI
     */
    private fun updateRoiPoints(id: Int, points: List<Offset>) {
        try {
            val currentState = _state.value
            currentState.roi.find { it.id == id }?.let { currentRoi ->
                val rois = currentState.roi.toMutableList()
                rois[rois.indexOf(currentRoi)] = currentRoi.copy(points = points)
                _state.update { it.copy(roi = rois) }
            }
        } catch (_: Exception) {
            _state.update {
                it.copy(error = DataError.Local.UNKNOWN.toUiText())
            }
        }
    }

    /**
     * Delete selected ROI from list
     *
     * @param id ID of the ROI to delete
     */
    private fun deleteRoi(id: Int) {
        try {
            val rois = _state.value.roi.toMutableList()
            rois.removeAll { it.id == id }
            _state.update { it.copy(roi = rois) }

        } catch (_: Exception) {
            _state.update {
                it.copy(error = DataError.Local.UNKNOWN.toUiText())
            }
        }
    }

    /**
     * Get roi configuration from the device
     */
    private fun getRoiConfiguration() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        val currentState = _state.value

        if (!currentState.isDeviceActive) {
            _state.update { it.copy(isLoading = false, error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()) }
            return@launch
        }

        runCatching {
            val res = repository.getRoiConfiguration(
                token = authentication.userToken,
                deviceId = authentication.deviceId
            )

            when (res) {
                is Result.Success -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            roi = res.data,
                            selectedRoi = null
                        )
                    }
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = res.error.toUiText()
                        )
                    }
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText()
                )
            }
        }
    }

    /**
     * Save roi configuration to the device
     */
    private fun saveRoiConfiguration() = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        val currentState = _state.value

        if (!currentState.isDeviceActive) {
            _state.update { it.copy(isLoading = false, error = DataError.Remote.SERVICE_UNAVAILABLE.toUiText()) }
            return@launch
        }

        runCatching {
            val res = repository.setRoiConfiguration(
                token = authentication.userToken,
                deviceId = authentication.deviceId,
                config = currentState.roi
            )

            when (res) {
                is Result.Success -> {
                    _state.update { it.copy(isLoading = false) }
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = res.error.toUiText()
                        )
                    }
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Local.UNKNOWN.toUiText()
                )
            }
        }
    }
}